import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ControlCenter } from "@/features/admin";
import { CaretRightIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { compact, percent } from "./format";

/**
 * The technical half of the verdict, each line a door into the page that explains it.
 * The response-time gauge is drawn against the same 1.5s / 3s bands the health check uses.
 */
export function TechPanel({ data }: { data: ControlCenter }) {
  const { latency, errors, synthetic } = data;
  const scaleMax = 4000;
  const p50 = Math.min(latency.p50, scaleMax);
  const p95 = Math.min(latency.p95, scaleMax);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Santé technique</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <div className="mb-2 flex items-baseline justify-between text-xs">
            <span className="font-medium">Attente avant réponse</span>
            <span className="text-muted-foreground tabular-nums">
              {compact(latency.samples)} tours mesurés
            </span>
          </div>
          <div className="relative h-3 w-full overflow-hidden rounded-full">
            <div className="absolute inset-y-0 left-0 w-[37.5%] bg-success/25" />
            <div className="absolute inset-y-0 left-[37.5%] w-[37.5%] bg-warning/25" />
            <div className="absolute inset-y-0 left-[75%] right-0 bg-danger/25" />
            {latency.samples ? (
              <>
                <div
                  className="absolute inset-y-0 w-1 rounded-full bg-foreground transition-all duration-700"
                  style={{ left: `calc(${(p50 / scaleMax) * 100}% - 2px)` }}
                  title={`Médiane ${latency.p50} ms`}
                />
                <div
                  className="absolute inset-y-0 w-1 rounded-full bg-foreground/50 transition-all duration-700"
                  style={{ left: `calc(${(p95 / scaleMax) * 100}% - 2px)` }}
                  title={`p95 ${latency.p95} ms`}
                />
              </>
            ) : null}
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground tabular-nums">
            <span>0 s</span>
            <span>1,5 s</span>
            <span>3 s</span>
            <span>4 s+</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground tabular-nums">
            médiane <b className="text-foreground">{latency.p50} ms</b> · p95{" "}
            <b className="text-foreground">{latency.p95} ms</b>
          </div>
        </div>

        <ul className="divide-y text-xs">
          <Row
            to="/admin/technical"
            label="Tours servis par le fallback LLM"
            value={`${compact(latency.fallback_turns)}`}
            hint={percent(latency.fallback_turns, latency.turns, 1)}
            bad={latency.turns > 0 && latency.fallback_turns / latency.turns > 0.1}
          />
          <Row
            to="/admin/tool-errors"
            label="Incidents d'outils"
            value={compact(errors.tool_incidents)}
            hint={percent(errors.tool_incidents, errors.tool_calls, 1)}
            bad={errors.tool_incidents > 0}
          />
          <Row
            to="/admin/external-calls"
            label="Échecs d'APIs externes"
            value={compact(errors.external_failures)}
            hint={percent(errors.external_failures, errors.external_calls, 1)}
            bad={
              errors.external_calls > 0 &&
              errors.external_failures / errors.external_calls > 0.05
            }
          />
          <Row
            to="/admin/synthetic-calls"
            label="Dernier appel de test"
            value={
              synthetic
                ? ({ ok: "OK", degraded: "Dégradé", failed: "Échec", error: "Erreur" }[
                    synthetic.status
                  ] ?? synthetic.status)
                : "—"
            }
            hint={
              synthetic
                ? new Date(synthetic.started_at).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "short",
                  })
                : ""
            }
            bad={!!synthetic && synthetic.status !== "ok"}
          />
        </ul>
      </CardContent>
    </Card>
  );
}

function Row({
  to,
  label,
  value,
  hint,
  bad,
}: {
  to: string;
  label: string;
  value: string;
  hint?: string;
  bad?: boolean;
}) {
  return (
    <li>
      <Link
        to={to}
        className="group flex items-center gap-2 py-2 hover:text-foreground"
      >
        <span className="text-muted-foreground group-hover:text-foreground">
          {label}
        </span>
        <span
          className={`ml-auto font-medium tabular-nums ${bad ? "text-danger" : ""}`}
        >
          {value}
        </span>
        {hint ? (
          <span className="w-12 text-right text-muted-foreground tabular-nums">
            {hint}
          </span>
        ) : null}
        <CaretRightIcon
          size={12}
          className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
        />
      </Link>
    </li>
  );
}
